/**
 * 展示格式化的唯一出口（spec 08 §5）。组件内禁止 toFixed / padStart / 手拼单位。
 *
 * 两条硬约束：
 *   1. 缺测点一律画 `—`，禁止折成 0 / ¥0.00 / 0.0%（Skill tokens.md 铁律 5）。
 *   2. 金额单位是微元整数（1 元 = 1e6），全程整数运算，浮点不碰钱（spec 00 §3.1-2）。
 */
import dayjs from 'dayjs';

/** 缺测点。全仓只有这一个来源。 */
export const DASH = '—';

type Nullish = null | undefined;

function isMissing(v: number | Nullish): v is Nullish {
  return v === null || v === undefined || !Number.isFinite(v);
}

const groupFormatter = new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 0 });

function group(intValue: number): string {
  return groupFormatter.format(intValue);
}

/**
 * 微元 → `¥12.35`（分位四舍五入）。
 * 走整数运算：micro → 分 → 元.分，全程不做浮点小数拼接。
 */
export function money(micro: number | Nullish): string {
  if (isMissing(micro)) return DASH;
  const cents = Math.round(micro / 10_000);
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  const yuan = Math.floor(abs / 100);
  const frac = abs % 100;
  return `${sign}¥${group(yuan)}.${String(frac).padStart(2, '0')}`;
}

/**
 * 微元 → `¥12`（取整到元）。图表刻度用——坐标轴上放两位小数会挤成一团，
 * 而且轴刻度本来就是量级参考，不是对账数字。
 */
export function moneyRounded(micro: number | Nullish): string {
  if (isMissing(micro)) return DASH;
  return `¥${group(Math.round(micro / 1_000_000))}`;
}

/** 微元 → `¥12.345678`，一微不差。对账、流水明细用。 */
export function moneyExact(micro: number | Nullish): string {
  if (isMissing(micro)) return DASH;
  const sign = micro < 0 ? '-' : '';
  const abs = Math.abs(Math.trunc(micro));
  const yuan = Math.floor(abs / 1_000_000);
  const frac = abs % 1_000_000;
  return `${sign}¥${group(yuan)}.${String(frac).padStart(6, '0')}`;
}

/**
 * 毫秒 → `m:ss`（≥1 小时给 `h:mm:ss`）。
 *
 * 注：Skill copy.md 规定时长展示为 `m:ss`，spec 08 §5 的示例写的是 `1分35秒`。
 * 按 08 §1「视觉与文案的唯一真源是 Skill」，这里以 Skill 为准；
 * 需要中文口径的地方用下面的 durationCn。
 */
export function duration(ms: number | Nullish): string {
  if (isMissing(ms) || ms < 0) return DASH;
  const total = Math.floor(ms / 1000);
  const s = total % 60;
  const m = Math.floor(total / 60) % 60;
  const h = Math.floor(total / 3600);
  const ss = String(s).padStart(2, '0');
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${ss}`;
  return `${m}:${ss}`;
}

/** 毫秒 → `1分35秒`。摘要、通知文案里比 m:ss 更好读。 */
export function durationCn(ms: number | Nullish): string {
  if (isMissing(ms) || ms < 0) return DASH;
  const total = Math.floor(ms / 1000);
  if (total < 60) return `${total}秒`;
  const s = total % 60;
  const m = Math.floor(total / 60) % 60;
  const h = Math.floor(total / 3600);
  if (h > 0) return s === 0 ? `${h}小时${m}分` : `${h}小时${m}分${s}秒`;
  return s === 0 ? `${m}分` : `${m}分${s}秒`;
}

/**
 * 占比 → `30.0%`。
 * 分母为 0 返回 `—` 而不是 `0.0%`：0 次拨打时的接通率是「无意义」，不是「零」。
 * 这条直接决定漏斗页的正确性（spec 08 §5）。
 */
export function percent(numerator: number | Nullish, denominator: number | Nullish): string {
  if (isMissing(numerator) || isMissing(denominator) || denominator === 0) return DASH;
  const tenths = Math.round((numerator / denominator) * 1000);
  const sign = tenths < 0 ? '-' : '';
  const abs = Math.abs(tenths);
  return `${sign}${group(Math.floor(abs / 10))}.${abs % 10}%`;
}

/**
 * 基点整数 → `33.3%`。1 基点 = 万分之一，10000bp = 100%。
 *
 * 后端下发率值一律用基点整数（字段名带 `_bp`），前端只做展示除法：
 * 比率用浮点存会在多级聚合后飘小数位，用整数基点则处处一致。
 * 缺测是 null 而不是 0——0 次拨打时的接通率是「无意义」，不是「零」。
 */
export function rateBp(bp: number | Nullish): string {
  if (isMissing(bp)) return DASH;
  const tenths = Math.round(bp / 10);
  const sign = tenths < 0 ? '-' : '';
  const abs = Math.abs(tenths);
  return `${sign}${group(Math.floor(abs / 10))}.${abs % 10}%`;
}

/** 整数 → `12,345`。 */
export function count(n: number | Nullish): string {
  if (isMissing(n)) return DASH;
  return group(Math.trunc(n));
}

/** 后端已脱敏的号码原样透出；空值画 `—`。前端不做脱敏，脱敏是服务端的事（spec 06 §4）。 */
export function phone(masked: string | Nullish): string {
  return masked ? masked : DASH;
}

/** RFC3339 → `09-02 10:30`。列表用。 */
export function datetime(iso: string | Nullish): string {
  if (!iso) return DASH;
  const d = dayjs(iso);
  return d.isValid() ? d.format('MM-DD HH:mm') : DASH;
}

/** RFC3339 → `2026-09-02 10:30:45`。详情、审计用。 */
export function datetimeFull(iso: string | Nullish): string {
  if (!iso) return DASH;
  const d = dayjs(iso);
  return d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : DASH;
}

/** RFC3339 → `2026-09-02`。 */
export function date(iso: string | Nullish): string {
  if (!iso) return DASH;
  const d = dayjs(iso);
  return d.isValid() ? d.format('YYYY-MM-DD') : DASH;
}

/** 任意可能缺失的文本 → 原值或 `—`。 */
export function text(v: string | Nullish): string {
  return v !== null && v !== undefined && v !== '' ? v : DASH;
}
